import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  Button,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { CopilotToggleCustomEvent, LiveAgentQueue } from '@bingads-webui-react/msa-copilot-common';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { compose, get, noop } from 'underscore';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withScope } from '@bingads-webui-react/scope-service';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';

import { useStyles } from './use-styles';
import { EmailInput } from './email';
import { STEPS, STEPS_CONFIG, OPTIONS_DECISIONS_MAP, DECISIONS, instrumentationSchema } from './config';
import { getConsentModeDialogStatus, updateConsentModeDecision, getErrorMessage } from './data-repository';

const RadioOptions = React.memo(({
  classes,
  i18n,
  radioList,
  value,
  onChange,
}) => (
  <RadioGroup
    className={classes.radioGroup}
    value={value}
    onChange={onChange}
  >
    {
      radioList.map(radio => (
        <Radio
          key={radio.value}
          value={radio.value}
          label={i18n.getString(radio.label)}
          className={classes.radioButton}
        />
      ))
    }
  </RadioGroup>
));

RadioOptions.propTypes = {
  classes: PropTypes.shape({
    radioGroup: PropTypes.string,
    radioButton: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  radioList: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ConsentModeDialogInternl = ({
  i18n,
  permissions,
  serverContext,
  instrumentation,
}) => {
  const isConsentModeEnformentEnabled = get(permissions, ['dynamic', 'ConsentModeEnforcement'], false);

  const classes = useStyles();
  const { CurrentAccount: { AdvertiserCustomerID }, CurrentCustomer: { Id } } = serverContext;
  const { aid } = queryString.parse(location.search);
  // Current customer is not the right parent customer
  const disallowRequest = aid && AdvertiserCustomerID !== Id;
  const hideDialog = !isConsentModeEnformentEnabled || disallowRequest;

  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.OPTIONS_STEP);
  const [agreeStatus, setAgreeStatus] = useState('agree');
  const [setupMethod, setSetupMethod] = useState('agent');
  const [acknowledgeStatus, setAcknowledgeStatus] = useState('accept');
  const [decision, setDecision] = useState(null);
  const [emails, setEmails] = useState('');
  const [validateResult, setValidationResult] = useState({ valid: false, message: '' });
  const [serverError, setServerError] = useState('');

  const logger = useMemo(() => {
    if (instrumentation && !instrumentation.ConsentModeEnforcementDialog) {
      instrumentation.addScenario(instrumentationSchema);
    }

    const backupLogger = {
      trace: noop,
      error: noop,
    };

    return get(instrumentation, 'ConsentModeEnforcementDialog')
      ? instrumentation.consentModeEnforcementDialog.create().dialogActivity.create()
      : backupLogger;
  }, [instrumentation]);

  const {
    title,
    description,
    radioList,
    templateModel = {},
  } = useMemo(() => STEPS_CONFIG[currentStep] || {}, [currentStep]);

  const triggerCopilotChat = useCallback(() => {
    const customEvent = new CopilotToggleCustomEvent({
      detail: {
        eventType: 'action',
        eventSource: 'consentModeEnforcement',
        eventData: {
          ActionId: 'liveAgentSupport',
          liveAgentParameters: {
            queue: LiveAgentQueue.ShoppingAndUET,
            problemDescription: i18n.getString(_TL_('Looking for help in UET consent mode setup')),
          },
        },
      },
    });
    document.dispatchEvent(customEvent);
  }, [i18n]);

  const onChangeEmail = useCallback((newValue, validation) => {
    setEmails(newValue);
    setValidationResult(validation);
  }, []);

  const makeDecision = useCallback(() => {
    const payload = {
      ConsentModeDecision: decision,
      EmailAddresses: emails,
    };

    logger.trace(`Consent mode decision: ${decision}, emails: ${emails}`);

    setIsLoading(true);

    return updateConsentModeDecision(AdvertiserCustomerID, payload).then(res => res)
      .catch(err => Promise.reject(err))
      .finally(() => {
        setIsLoading(false);
      });
  }, [decision, emails, logger, AdvertiserCustomerID]);

  const onConfirmDecision = useCallback(() => {
    makeDecision().then(() => {
      logger.trace('Get consent mode status succeed');
      setCurrentStep(STEPS.DONE_STEP);
    }).catch((err) => {
      logger.error(`Get consent mode status failed: ${JSON.stringify(err)}`);
      setServerError(i18n.getString(getErrorMessage(err)));
    });
  }, [makeDecision, logger, i18n]);

  const renderDescription = useCallback(() => {
    let finalizedDescription = description;
    if (currentStep === STEPS.DONE_STEP && typeof description === 'object') {
      finalizedDescription = description[decision];
    }

    return (
      <ReactTemplate
        model={templateModel || {}}
        template={i18n.getString(finalizedDescription)}
      />
    );
  }, [currentStep, decision, description, i18n, templateModel]);

  const renderOptions = () => {
    if (currentStep === STEPS.OPTIONS_STEP) {
      return (
        <RadioOptions
          classes={classes}
          i18n={i18n}
          radioList={radioList}
          value={agreeStatus}
          onChange={(_, data) => setAgreeStatus(data.value)}
        />
      );
    } else if (currentStep === STEPS.AGREE_STEP) {
      const onChangeOption = (_, data) => {
        setSetupMethod(data.value);
        setDecision(OPTIONS_DECISIONS_MAP[data.value]);
      };

      return (
        <>
          <RadioOptions
            classes={classes}
            i18n={i18n}
            radioList={radioList}
            value={setupMethod}
            onChange={onChangeOption}
          />
          {
            setupMethod === 'email' && (
              <EmailInput
                className={classes.emailContainer}
                label={i18n.getString(_TL_('Add email'))}
                placeholder={i18n.getString(_TL_('Add up to 5 email recipients, separated by semicolons'))}
                hint={i18n.getString(_TL_('In addition to the email addresses entered, all super admins will receive setup instructions.'))}
                value={emails}
                onChange={onChangeEmail}
              />
            )
          }
        </>
      );
    } else if (currentStep === STEPS.DISAGREE_STEP) {
      const onChangeOption = (_, data) => {
        setAcknowledgeStatus(data.value);
        setDecision(OPTIONS_DECISIONS_MAP[data.value]);
      };

      return (
        <RadioOptions
          classes={classes}
          i18n={i18n}
          radioList={radioList}
          value={acknowledgeStatus}
          onChange={onChangeOption}
        />
      );
    }

    return null;
  };

  const renderBottomBtns = () => {
    if (currentStep === STEPS.OPTIONS_STEP) {
      const onNext = () => {
        if (agreeStatus === 'agree') {
          setCurrentStep(STEPS.AGREE_STEP);
        } else {
          setCurrentStep(STEPS.DISAGREE_STEP);
        }
      };

      return (
        <Button appearance="primary" onClick={onNext}>
          {i18n.getString(_TL_('Continue'))}
        </Button>
      );
    } else if (currentStep === STEPS.AGREE_STEP) {
      return (
        <>
          <Button appearance="primary" onClick={onConfirmDecision} disabled={setupMethod === 'email' && !validateResult.valid}>
            {i18n.getString(_TL_('Confirm'))}
          </Button>
          <Button
            appearance="secondary"
            onClick={() => setCurrentStep(STEPS.OPTIONS_STEP)}
            className={classes.buttonSpacing}
          >
            {i18n.getString(_TL_('Back'))}
          </Button>
        </>
      );
    } else if (currentStep === STEPS.DISAGREE_STEP) {
      return (
        <>
          <Button appearance="primary" onClick={onConfirmDecision}>
            {i18n.getString(_TL_('Confirm'))}
          </Button>
          <Button
            appearance="secondary"
            onClick={() => setCurrentStep(STEPS.OPTIONS_STEP)}
            className={classes.buttonSpacing}
          >
            {i18n.getString(_TL_('Back'))}
          </Button>
        </>
      );
    }

    return (
      <Button
        appearance="primary"
        onClick={() => {
        if (decision === DECISIONS.CONTACT_AGENT) {
          triggerCopilotChat();
        }

        setShouldShowDialog(false);
      }}
      >
        {i18n.getString(_TL_('Done'))}
      </Button>
    );
  };

  useEffect(() => {
    if (!hideDialog) {
      getConsentModeDialogStatus(AdvertiserCustomerID).then((res = {}) => {
        logger.trace(`Get consent mode status succeed: ${res}`);

        const { NeedShowPopUp } = res;
        setShouldShowDialog(!!NeedShowPopUp);

        if (NeedShowPopUp) {
          logger.trace('Show consent mode dialog');
        }
      }).catch((err) => {
        logger.error(`Get consent mode status failed: ${JSON.stringify(err)}`);
      });
    }
  }, [AdvertiserCustomerID, logger, hideDialog]);

  // Update decision when get into decision step first time
  useEffect(() => {
    if (currentStep === STEPS.AGREE_STEP && !decision) {
      setDecision(OPTIONS_DECISIONS_MAP.agent);
    } else if (currentStep === STEPS.DISAGREE_STEP && !decision) {
      setDecision(OPTIONS_DECISIONS_MAP.accept);
    }
  }, [currentStep, decision]);

  if (hideDialog) {
    return null;
  }

  return (
    <Dialog open={shouldShowDialog}>
      <DialogSurface className={classes.dialogSurface}>
        <UiBlocker
          blocking={isLoading}
          loadingText={i18n.getString(_TL_('Updating your decision...'))}
        >
          <DialogBody>
            <DialogTitle>{i18n.getString(title)}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <div className={classes.description}>
                {renderDescription()}
              </div>
              {renderOptions()}
              {
                (currentStep === STEPS.AGREE_STEP || currentStep === STEPS.DISAGREE_STEP) && serverError
                  && (
                    <MessageBar className={classes.serverErrorMessage} intent="error">
                      <MessageBarBody>
                        {serverError}
                      </MessageBarBody>
                    </MessageBar>
                  )
              }
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              {renderBottomBtns()}
            </DialogActions>
          </DialogBody>
        </UiBlocker>
      </DialogSurface>
    </Dialog>
  );
};

ConsentModeDialogInternl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      AdvertiserCustomerID: PropTypes.number,
    }).isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
};

export const ConsentModeDialog = compose(
  withInstrumentation,
  withScope,
  withI18nConsumer,
  withPreference,
  withPermissions
)(ConsentModeDialogInternl);
